import { createContext, useContext, useEffect, useState } from "react";
import { PromiseLoaderProps, PromiseLoader, useContent } from "react-builder";
import { useProductApi } from "../custom-hooks/useProductApi";
import { ElearnChapter, ElearnLesson } from "../types/e-learning-types";
import { ElearnLevel, ElearnUserData } from "../types/e-learning-types";

export const useLanguage: () => string = () => {
  const [userLanguage, setUserLanguage] = useState<string>("");
  useEffect(() => {
    const Language: string = navigator.language.split("-")[0].toUpperCase();
    setUserLanguage(Language);
    // console.log("User's language:", userLanguage);
  }, []);
  return userLanguage;
};

interface IUserData {
  userData: Promise<ElearnUserData> | undefined;
  likedLessons: string[];
  watchedChapters: string[];
  watchedLessons: string[];
  updateUserDataArrays: (key: string, updatedArray: string[]) => void;
}

export const userDataContext = createContext<IUserData>({} as any);

export const UserDataContextProvider = (props: { children: any }) => {
  const userDataAPI = useProductApi<ElearnUserData>();
  const [userData, setUserData] = useState<Promise<ElearnUserData>>();
  const [likedLessons, setLikedLessons] = useState<string[]>([]);
  const [watchedLessons, setWatchedLessons] = useState<string[]>([]);
  const [watchedChapters, setWatchedChapters] = useState<string[]>([]);

  const updateUserDataArrays = (key: string, updatedArray: string[]) => {
    switch (key) {
      case "watchedChapters":
        setWatchedChapters([...updatedArray]);
        break;
      case "watchedLessons":
        setWatchedLessons([...updatedArray]);
        break;
      case "likedLessons":
        setLikedLessons([...updatedArray]);
        break;
      default:
        console.warn(
          "wrong string key to update user data array in useElearningData at updateUserDataArrays"
        );
        break;
    }
  };
  //   fetch user data
  useEffect(() => {
    if (!userDataAPI || userData) return;
    setUserData(userDataAPI.single(`elearn/ElearnUserData`));
  }, [userDataAPI]);

  // set user data arrays after fetch
  useEffect(() => {
    const fetchUserDataLessons = async () => {
      if (!userData) return;
      const response = await userData;
      setLikedLessons(response.likedLessons);
      setWatchedLessons(response.watchedLessons);
      setWatchedChapters(response.watchedChapters);
    };
    fetchUserDataLessons();
  }, [userData]);

  return (
    <userDataContext.Provider
      value={{
        userData,
        watchedLessons,
        likedLessons,
        watchedChapters,
        updateUserDataArrays,
      }}
    >
      {props.children}
    </userDataContext.Provider>
  );
};

export const useLevelsData: () => {
  levels: Promise<ElearnLevel[]> | undefined;
  watchedLevels: string[];
} = () => {
  const levelAPI = useProductApi<ElearnLevel>();
  const [levels, setLevels] = useState<Promise<ElearnLevel[]>>();
  const [watchedLevels, setWatchedLevels] = useState<string[]>([]);
  const userLanguage = useLanguage();
  const { userData } = useContext(userDataContext);

  //   fetch levels
  useEffect(() => {
    // console.log('elearn', elearn);
    if (!levelAPI || !userLanguage) return;
    setLevels(
      levelAPI
        .list(`elearn/ElearnLevel/${userLanguage}`, {
          populate: "thumb thumbMobile cover coverMobile",
        })
        .then((r) => r.list)
    );
  }, [levelAPI, userLanguage]);

  // set watchedLevels
  useEffect(() => {
    const checkWatchedLevels = async () => {
      const watchedChapters = await userData?.then((r) => r.watchedChapters);
      const fetchedLevels = await levels;
      if (fetchedLevels && watchedChapters)
        setWatchedLevels(findWatchedLevels(watchedChapters, fetchedLevels));
    };
    checkWatchedLevels();
  }, [levels, userData]);

  function findWatchedLevels(watchedChapters: string[], levels: ElearnLevel[]): string[] {
    return levels
      .filter((level) => level.chapters.every((chapterId) => watchedChapters.includes(chapterId)))
      .map((level) => level._id);
  }

  return {
    levels,
    watchedLevels,
  };
};

export const LevelsElearnProvider = (props: Omit<PromiseLoaderProps<ElearnLevel[]>, "promise">) => {
  const { levels } = useLevelsData();
  const [promise, setPromise] = useState<Promise<ElearnLevel[]>>();
  useEffect(() => {
    if (!levels) return;
    setPromise(levels);
  }, [levels]);

  return <PromiseLoader<ElearnLevel[]> {...props} promise={promise}></PromiseLoader>;
};

export const ChaptersElearnProvider = (
  props: Omit<PromiseLoaderProps<ElearnChapter[]>, "promise"> & { levelId: string }
) => {
  const chapterAPI = useProductApi<ElearnChapter>();
  const [promise, setPromise] = useState<Promise<ElearnChapter[]>>();
  const userLanguage = useLanguage();
  useEffect(() => {
    if (!chapterAPI || !userLanguage) return;
    setPromise(
      chapterAPI
        .list(`elearn/ElearnChapter/${userLanguage}`, {
          query: props.levelId ? JSON.stringify({ elearnLevel: props.levelId }) : "",
          populate: "thumb thumbCards",
        })
        .then((r) => r.list)
    );
  }, [chapterAPI, props.levelId, userLanguage]);

  return <PromiseLoader<ElearnChapter[]> {...props} promise={promise}></PromiseLoader>;
};

export const LessonsElearnProvider = (
  props: Omit<PromiseLoaderProps<ElearnLesson[]>, "promise"> & { chapterId: string }
) => {
  const lessonsAPI = useProductApi<ElearnLesson>();
  const [promise, setPromise] = useState<Promise<ElearnLesson[]>>();
  const userLanguage = useLanguage();
  useEffect(() => {
    if (!lessonsAPI || !userLanguage) return;
    setPromise(
      lessonsAPI
        .list(`elearn/ElearnLesson/${userLanguage}`, {
          populate: "video",
          query: props.chapterId ? JSON.stringify({ elearnChapter: props.chapterId }) : "",
        })
        .then((r) => r.list)
    );
  }, [lessonsAPI, props.chapterId, userLanguage]);

  return <PromiseLoader<ElearnLesson[]> {...props} promise={promise}></PromiseLoader>;
};

// export class Model<T> {
//   readonly base = "";
//   private cache: { [key: string]: Promise<any> } = {};
//   constructor(private config: { server: string; model: string; auth: IPAuthContext }) {}

//   get(config?: { path?: string; query?: any; headers?: any }): Promise<T[]> {
//     return (
//       this.cache[`${config?.path}/${new URLSearchParams(config?.query).toString()}`] ||
//       (this.cache[`${config?.path}/${new URLSearchParams(config?.query).toString()}`] =
//         this.request({ ...config, action: "GET" }))
//     );
//   }

//   post(config: { path?: string; body: Partial<T>; headers?: any }) {
//     return this.request({ ...config, action: "POST" });
//   }

//   put(config: { path?: string; body: T; headers: any }) {
//     return this.request({ ...config, action: "PUT" });
//   }

//   delete(config: { path?: string; headers?: any }) {
//     return this.request({ ...config, action: "DELETE" });
//   }

//   request(config: {
//     path?: string;
//     action: string;
//     query?: any;
//     body?: any;
//     headers?: HeadersInit | undefined;
//   }) {
//     // console.log(this.config.auth?.getSession());

//     return (
//       fetch(
//         `${this.config?.server}rest/elearn/10/${this.config.model}/${config.path ?? ""}${
//           config.query ? `?${new URLSearchParams(config.query).toString()}` : ""
//         }`,
//         {
//           headers: {
//             "content-type": "application/json",
//             ...config.headers,
//             Authorization: `Bearer ${this.config.auth?.getSession()?.token ?? ""}`,
//           },
//           body: config.body ? JSON.stringify(config.body ?? {}) : null,
//           method: config.action,
//         }
//       )
//         .then((r) => {
//           return r;
//         })
//         // .catch((e) => {
//         //     console.log(e);

//         // })
//         .then((res) =>
//           res.status == 200 ? (config.action != "DELETE" ? res.json() : res.text()) : res.text()
//         )
//     );
//   }
// }

// export class ElearningApi {
//   constructor(private server: string, private auth: IPAuthContext) {}

//   readonly lesson = new Model<Lesson>({ server: this.server, model: "lessons", auth: this.auth });
//   readonly chapter = new Model<Chapter>({
//     server: this.server,
//     model: "chapters",
//     auth: this.auth,
//   });
//   readonly level = new Model<Level>({ server: this.server, model: "levels", auth: this.auth });
//   readonly likedLessons = new Model<LikedLesson>({
//     server: this.server,
//     model: "likedLessons",
//     auth: this.auth,
//   });
//   readonly watchedLessons = new Model<LikedLesson>({
//     server: this.server,
//     model: "watchedLessons",
//     auth: this.auth,
//   });
//   readonly watchedChapters = new Model<LikedLesson>({
//     server: this.server,
//     model: "watchedChapters",
//     auth: this.auth,
//   });
//   readonly watchedLevels = new Model<WatchedLevel>({
//     server: this.server,
//     model: "watchedLevels",
//     auth: this.auth,
//   });
// }

// export const useElearning = singletonHook<ElearningApi | undefined>(undefined, function () {
//   let config = useContext(DomainConfigContext);
//   let auth = useContext(ProductServerAuthContext);
//   const [api, setApi] = useState<ElearningApi | undefined>(
//     config?.apiEndpoint ? new ElearningApi(config?.apiEndpoint!, auth) : undefined
//   );

//   useEffect(() => {
//     setApi(config?.apiEndpoint ? new ElearningApi(config?.apiEndpoint!, auth) : undefined);
//   }, [auth.isLoggedIn]);

//   return api;
// });
